import { useTranslation } from "react-i18next";
import ArrowLonRightIcon from "../../svg/ArrowLongRightIcon";
import { useState } from "react";

interface CompanyFormData {
  companyName: string;
  vatNumber: string;
  addressLine1: string;
  addressLine2: string;
  addressNumber: string;
  addressNumber2: string;
  postalCode: string;
  city: string;
  country: string;
  email: string;
  web: string;
  telephone: string;
  metadataNumber: string;
  metadataNumberText: string;
  metadataFormattedNumber: string;
  metadataStartDate: string;
  metadataType: string;
  metadataLegalForm: string;
  metadataJuridicalSituation: string;
  metadataNACECode: string;
  user_id: string | number;
  billingEmail: string;
  billingAddressStreet: string;
  billingAddressNumber: string;
  billingAddressZip: string;
  billingAddressCity: string;
  billingAddressCountry: string;
  invoicingCycle: string;
  iban: string;
}

export interface CompanyFormProps {
  companyData: CompanyFormData;
  isEditable: boolean;
  loading: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onProceedToBilling: () => void;
}

export const CompanyForm = ({
  companyData,
  isEditable,
  loading,
  onInputChange,
  onProceedToBilling,
}: CompanyFormProps) => {
  const { t } = useTranslation("AddCompany");
  const [showValidation, setShowValidation] = useState(false);

  const requiredFields = [
    "companyName",
    "vatNumber",
    "addressLine1",
    "addressNumber",
    "postalCode",
    "city",
    "email",
    "metadataNumber",
  ];

  // const missingFields = requiredFields.filter((field) => !companyData[field as keyof CompanyFormData]);

  const getInputClass = (field: string) =>
    `border py-3 px-4 w-full rounded-md ${isEditable ? "bg-white" : "bg-gray-50 cursor-not-allowed"} ${
      showValidation && !companyData[field as keyof CompanyFormData] ? "border-orange-400" : "border-gray-300"
    }`;

  const handleProceed = () => {
    const missing = requiredFields.filter((field) => !companyData[field as keyof CompanyFormData]);
    setShowValidation(true);
    if (missing.length === 0) onProceedToBilling();
  };

  const isRequired = (field: string) => requiredFields.includes(field);

  return (
    <div className="flex flex-col gap-6 font-semibold">
      <div className="flex justify-between gap-8">
        <div className="flex-1">
          <p className="mb-2">
            {t("companyName")}
            {isRequired("companyName") && <span className="text-orange-500 ml-1">*</span>}
          </p>
          <input
            className={getInputClass("companyName")}
            type="text"
            name="companyName"
            value={companyData.companyName}
            onChange={onInputChange}
            readOnly={!isEditable}
          />
        </div>
        <div className="flex-1">
          <p className="mb-2">
            {t("vatNumber")}
            {isRequired("vatNumber") && <span className="text-orange-500 ml-1">*</span>}
          </p>
          <input
            className={getInputClass("vatNumber")}
            type="text"
            name="vatNumber"
            value={companyData.vatNumber}
            onChange={onInputChange}
            readOnly={!isEditable}
          />
        </div>
      </div>

      <div className="flex justify-between gap-8">
        <div className="flex-1">
          <p className="mb-2">
            {t("addressLine")}
            {isRequired("addressLine1") && <span className="text-orange-500 ml-1">*</span>}
          </p>
          <input
            className={getInputClass("addressLine1")}
            type="text"
            name="addressLine1"
            value={companyData.addressLine1}
            onChange={onInputChange}
            readOnly={!isEditable}
          />
        </div>
        <div className="flex-3">
          <p className="mb-2">
            {t("streetNumber")}
            {isRequired("addressNumber") && <span className="text-orange-500 ml-1">*</span>}
          </p>
          <input
            className={getInputClass("addressNumber")}
            name="addressNumber"
            type="text"
            value={companyData.addressNumber}
            onChange={onInputChange}
            readOnly={!isEditable}
          />
        </div>
      </div>

      <div className="flex justify-between gap-8">
        <div className="flex-1">
          <p className="mb-2">
            {t("postalCode")}
            {isRequired("postalCode") && <span className="text-orange-500 ml-1">*</span>}
          </p>
          <input
            className={getInputClass("postalCode")}
            type="text"
            name="postalCode"
            value={companyData.postalCode}
            onChange={onInputChange}
            readOnly={!isEditable}
          />
        </div>
        <div className="flex-1">
          <p className="mb-2">
            {t("city")}
            {isRequired("city") && <span className="text-orange-500 ml-1">*</span>}
          </p>
          <input
            className={getInputClass("city")}
            type="text"
            name="city"
            value={companyData.city}
            onChange={onInputChange}
            readOnly={!isEditable}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1">
        <p className="mb-2">
          {t("companyEmail")}
          {isRequired("email") && <span className="text-orange-500 ml-1">*</span>}
        </p>
        <input
          className={getInputClass("email")}
          type="email"
          name="email"
          value={companyData.email}
          onChange={onInputChange}
          readOnly={!isEditable ? true : false}
          placeholder={t("companyEmailPlaceholder")}
        />
        <span className="text-xs text-primary-500 mt-1">
          {t("companyEmailInfo", { defaultValue: "This email will be used for info." })}
        </span>
      </div>

      {/* {showValidation && missingFields.length > 0 && (
        <div className="bg-orange-50 border border-orange-400 text-orange-700 rounded p-3 mb-2 text-sm">
          {t("missingFieldsMessage", {
            fields: missingFields.map((f) => t(f)).join(", "),
            defaultValue: "Please fill in: " + missingFields.join(", "),
          })}
        </div>
      )} */}

      <div className="flex justify-end">
        <button
          className="py-3 px-4 rounded-md bg-primary-600 hover:bg-primary-700 text-white flex gap-3 items-center"
          onClick={handleProceed}
          disabled={loading}
        >
          {t("proceedToBilling")} <ArrowLonRightIcon />
        </button>
      </div>
      <div className="text-xs text-primary-500 ">
        <span className="text-orange-500"></span>{" "}
        {t("mandatoryFieldsNote", { defaultValue: "Fields marked with * are mandatory." })}
      </div>
    </div>
  );
};
